/**
 * MOMENTLISTANS DATALAGER — det momentskärmen läser och skriver genom.
 *
 * =========================================================================
 * DET FINNS INGEN LISTRUTT FÖR MOMENT, OCH DET ÄR MED FLIT.
 * shared/routes.ts: SYNC_PUSH och SYNC_PULL är den ENDA kanalen mot
 * företagsdata — "inga skriv-endpoints per tabell", och ingen läs-endpoint
 * heller. Listan hämtas därför ur en SYNC_PULL och ändringarna går genom
 * SYNC_PUSH med 'put:task' och 'delete:task', båda MANAGER_ONLY i SYNC_RULES.
 * Steg 5 äger den riktiga lagringen (IndexedDB, utkorg, bakgrundssynk). Tills
 * dess hämtar modulen HELA firman från since = 0 vid behov och håller resultatet
 * i minnet. Medvetet enkelt och medvetet tillfälligt: allt ligger bakom
 * `useMomentlager`, så den dag lagret finns byts innanmätet utan att skärmen rör
 * sig. Samma mönster som arbetsorder-data.ts.
 * =========================================================================
 *
 * LAGRET BÄR BARA `task`. En pull ger sexton tabeller i full vy, men den här
 * skärmen har med exakt en av dem att göra. Att lyfta in fler rader i lagret
 * hade gjort det till en andra kopia av arbetsorderns lager, och två lager som
 * beskriver samma firma är två lager som förr eller senare säger olika saker.
 *
 * `vy` FÖLJER MED UT. TaskRow är samma rad i alla tre vyerna (beslut 16 skalar
 * bort priser, inte momenttexter), så listan går att visa för alla — men bara
 * chefen får skriva. Skärmen läser rollen ur sessionen och inte ur lagret; `vy`
 * finns här för att en bokförare ska kunna få rätt besked om VARFÖR knapparna
 * saknas.
 */

import { useCallback, useEffect, useState } from 'react';
import { SYNC_PULL, SYNC_PUSH } from '@delat/routes';
import { SYNC_PULL_MAX_LIMIT, asMillis, asSeq } from '@delat/types';
import type {
  ClientView,
  CompanyId,
  Millis,
  OpId,
  Seq,
  SyncOp,
  SyncPullResponse,
  SyncPushResponse,
  TaskId,
  TaskRow,
  Version,
} from '@delat/types';
import { apifel, enhetsId, granska, klienten, lyckades, misslyckades, nyUlid } from '../api';
import type { Klartidfel, Resultat } from '../api';
import { granskaSyncPull, granskaSyncPush } from '../api/granskare/moment';
import { registreraFirmaminne } from '../app/firmaminne';

/* == Gränsen som sitter i schemat ========================================
   schema/company.sql: CHECK (length(name) BETWEEN 1 AND 60) på task, och
   worker/src/do/company-store.ts prövar samma tal med requireText(..., {max:60}).
   TALET STÅR I api/konstanter.ts, bredvid de andra avskrivna CHECK-gränserna,
   och läses därifrån — det ska finnas på exakt ett ställe i klienten. Raden
   nedan är kvar som re-export så att skärmen inte behöver två importvägar.
   ======================================================================= */

export { MAX_MOMENTNAMN_TECKEN } from '../api/konstanter';

/* == Lagret ============================================================== */

export interface Momentlager {
  readonly vy: ClientView;
  /** Firmans levande moment. Osorterade — skärmen bestämmer ordningen. */
  readonly moment: readonly TaskRow[];
  /**
   * true = firman har fler rader än MAX_SIDOR * SYNC_PULL_MAX_LIMIT och allt kom
   * inte med. Skärmen SKA säga det: ett moment som saknas i listan utan att
   * något felade är värre än ett felmeddelande.
   */
  readonly avkortat: boolean;
}

interface Samlare {
  vy: ClientView;
  readonly moment: Map<string, TaskRow>;
}

const tomSamlare = (): Samlare => ({ vy: 'worker', moment: new Map() });

/**
 * Högsta seq vinner. En pull som görs i flera vändor kan få samma rad två
 * gånger om någon skriver mitt i, och då är den med högst seq den senaste
 * (schema/company.sql: varje accepterad skrivning får ett nytt seq).
 */
const slaSamman = (karta: Map<string, TaskRow>, rader: readonly TaskRow[]): void => {
  for (const rad of rader) {
    const gammal = karta.get(rad.id);
    if (gammal === undefined || rad.seq > gammal.seq) karta.set(rad.id, rad);
  }
};

/**
 * INGEN GRENING PÅ `view` HÄR, och det är inte en genväg. De tre
 * ändringsmängderna bär olika tabeller, men `task` finns i ALLA TRE och är
 * `readonly TaskRow[]` i alla tre — beslut 16 skalar bort priser, inte
 * momenttexter. Uppslaget på en union där varje gren har samma fält med samma
 * typ är därför lika säkert som en gren. Först den dag TaskRow skiljer sig åt
 * mellan vyerna faller det här, i tsc, och då ska grenarna skrivas ut.
 */
const lagg = (s: Samlare, svar: SyncPullResponse): void => {
  s.vy = svar.view;
  slaSamman(s.moment, svar.changes.task);
};

/** Mjukraderade rader kommer med i pullen just för att kunna tas bort här. */
const fardigt = (s: Samlare, avkortat: boolean): Momentlager => ({
  vy: s.vy,
  moment: [...s.moment.values()].filter((rad) => rad.deleted_at === null),
  avkortat,
});

/* == Hämtningen ========================================================== */

/**
 * Taket för hur många vändor en hämtning tar. 40 * 1000 rader räcker långt för
 * en firma med fem man; blir det fler säger `avkortat` det rakt ut i stället för
 * att listan tyst blir ofullständig.
 */
const MAX_SIDOR = 40;

const hamtaAllt = async (
  firmaId: CompanyId,
  signal: AbortSignal,
): Promise<Resultat<Momentlager>> => {
  const samlare = tomSamlare();
  let since: Seq = asSeq(0);
  let avkortat = false;

  for (let varv = 0; varv < MAX_SIDOR; varv += 1) {
    const svar = await klienten.anrop(
      SYNC_PULL,
      {
        params: { company_id: firmaId },
        data: { since, limit: SYNC_PULL_MAX_LIMIT },
        signal,
      },
      granskaSyncPull,
    );
    if (!svar.ok) return misslyckades(svar.fel);
    lagg(samlare, svar.varde);

    // has_more = true betyder BARA att fler seq återstår, inte att den här
    // vändan gav rader (SyncPullResponse). En tom vända med has_more är NORMAL i
    // montörs- och gästläge och måste följas av en ny pull på next_since.
    if (!svar.varde.has_more) break;
    if (svar.varde.next_since <= since) {
      // MARKÖREN STÅR STILL MEDAN SERVERN SÄGER has_more: det finns rader vi
      // inte fick. Ett tyst avbrott hade ritat momentlistan som om den vore hel.
      avkortat = true;
      break;
    }
    since = svar.varde.next_since;
    avkortat = varv === MAX_SIDOR - 1;
  }

  return lyckades(fardigt(samlare, avkortat));
};

/* == Ett litet minne =====================================================
   Momentlistan hämtas om vid varje besök på skärmen, och en firma laddas om
   från början varje gång. Minnet är frivilligt: `glomMomentlagret()` efter varje
   skrivning, och en hämtning äldre än FARSKHET_MS görs om ändå.
   ======================================================================= */

const FARSKHET_MS = 30_000;

let minne: { firmaId: CompanyId; lager: Momentlager; hamtat: number } | null = null;

export const glomMomentlagret = (): void => {
  minne = null;
};

/** Töms vid utloggning och firmabyte tillsammans med de andra lagren. */
registreraFirmaminne(glomMomentlagret);

const farskt = (firmaId: CompanyId): Momentlager | null =>
  minne !== null && minne.firmaId === firmaId && Date.now() - minne.hamtat < FARSKHET_MS
    ? minne.lager
    : null;

export type Momentlage =
  | { readonly status: 'laddar' }
  | { readonly status: 'fel'; readonly fel: Klartidfel }
  | { readonly status: 'klart'; readonly lager: Momentlager };

export interface Momentstyrning {
  readonly lage: Momentlage;
  /** Hämtar om, alltid mot nätet. Efter en skrivning eller på "Försök igen". */
  readonly laddaOm: () => void;
}

export const useMomentlager = (firmaId: CompanyId): Momentstyrning => {
  const [lage, sattLage] = useState<Momentlage>(() => {
    const sparat = farskt(firmaId);
    return sparat === null ? { status: 'laddar' } : { status: 'klart', lager: sparat };
  });
  const [omgang, sattOmgang] = useState(0);

  const laddaOm = useCallback(() => {
    glomMomentlagret();
    sattLage({ status: 'laddar' });
    sattOmgang((n) => n + 1);
  }, []);

  useEffect(() => {
    const sparat = farskt(firmaId);
    if (sparat !== null) {
      sattLage({ status: 'klart', lager: sparat });
      return;
    }
    const styrning = new AbortController();
    let vaken = true;
    sattLage({ status: 'laddar' });

    void (async () => {
      const svar = await hamtaAllt(firmaId, styrning.signal);
      if (!vaken) return;
      if (!svar.ok) {
        // Ett avbrutet anrop är inte ett fel — skärmen lämnades.
        if (svar.fel.slag === 'nat' && svar.fel.avbrutet) return;
        sattLage({ status: 'fel', fel: svar.fel });
        return;
      }
      minne = { firmaId, lager: svar.varde, hamtat: Date.now() };
      sattLage({ status: 'klart', lager: svar.varde });
    })();

    return () => {
      vaken = false;
      styrning.abort();
    };
  }, [firmaId, omgang]);

  return { lage, laddaOm };
};

/* == Skrivningen ========================================================= */

/**
 * OP_ID ÄR ANROPARENS (beslut 09). Mynta ETT när användaren trycker Spara, spara
 * det med det du ska skicka och använd SAMMA vid varje omsändning — annars blir
 * varje omtag en ny skrivning i stället för ett uppspelat svar.
 */
export const nyttOpId = (): OpId => granska.opId(nyUlid(), 'op_id');
export const nyttMomentId = (): TaskId => granska.taskId(nyUlid(), 'task.id');
export const nu = (): Millis => asMillis(Date.now());

/**
 * DE TRE OPERATIONERNA SKÄRMEN KAN SKICKA.
 *
 * base_version = null betyder "raden ska inte finnas" och ger alltid version 1.
 * En ändring och en radering bär radens NUVARANDE version — stämmer den inte
 * svarar servern SYNC_CONFLICT, vilket är precis rätt svar när två telefoner
 * bygger om samma momentlista samtidigt.
 *
 * TaskWrite BÄR BARA name OCH rot_eligible. Appboken föreslår en tid per moment;
 * den finns inte i kontraktet och skickas därför inte.
 */
export const nyttMomentOp = (
  id: TaskId,
  namn: string,
  gerRot: boolean,
  opId: OpId,
): SyncOp => ({
  op_id: opId,
  client_time: nu(),
  kind: 'put',
  base_version: null,
  table: 'task',
  id,
  row: { name: namn, rot_eligible: gerRot ? 1 : 0 },
});

export const andraMomentOp = (
  rad: TaskRow,
  namn: string,
  gerRot: boolean,
  opId: OpId,
): SyncOp => ({
  op_id: opId,
  client_time: nu(),
  kind: 'put',
  base_version: rad.version,
  table: 'task',
  id: rad.id,
  row: { name: namn, rot_eligible: gerRot ? 1 : 0 },
});

export const raderaMomentOp = (id: TaskId, radensVersion: Version, opId: OpId): SyncOp => ({
  op_id: opId,
  client_time: nu(),
  kind: 'delete',
  base_version: radensVersion,
  table: 'task',
  id,
});

export const skickaOps = async (
  firmaId: CompanyId,
  ops: readonly SyncOp[],
  signal?: AbortSignal,
): Promise<Resultat<SyncPushResponse>> =>
  klienten.anrop(
    SYNC_PUSH,
    {
      params: { company_id: firmaId },
      data: { device_id: enhetsId(), ops },
      ...(signal === undefined ? {} : { signal }),
    },
    granskaSyncPush,
  );

/**
 * FÖRSTA OPERATIONEN SOM FÖLL, som ett fel gränssnittet kan visa.
 *
 * Ett giltigt kuvert svarar ALLTID 200 även om varje operation föll, så
 * `svar.ok` säger ingenting om utfallet — det står i SyncOpResult. Texten hämtas
 * ur shared/errors.ts genom apifel() och ALDRIG ur serverns sträng (api/fel.ts).
 */
export const forstaMisslyckade = (svar: SyncPushResponse): Klartidfel | null => {
  for (const resultat of svar.results) {
    if (resultat.status !== 'failed') continue;
    const kropp = resultat.error;
    // Grenarna finns för att de två SLUTNA detaljformerna ska behålla sin typ
    // hela vägen in i apifel(); se ApifelFor i api/fel.ts.
    if (kropp.code === 'ROW_INVOICED') {
      return apifel('ROW_INVOICED', kropp.detail, kropp.message_sv);
    }
    if (kropp.code === 'PRICE_MISSING') {
      return apifel('PRICE_MISSING', kropp.detail, kropp.message_sv);
    }
    return apifel(kropp.code, kropp.detail, kropp.message_sv);
  }
  return null;
};

/** true = operationen är gjord och ska INTE skickas igen ur samma plan. */
export const arKlar = (svar: SyncPushResponse, opId: OpId): boolean =>
  svar.results.some(
    (r) => r.op_id === opId && (r.status === 'applied' || r.status === 'duplicate'),
  );

/* == Uppslag skärmen gör om och om igen ================================== */

/**
 * MOMENTEN I BOKSTAVSORDNING. Kontraktet har ingen sorteringskolumn på task, och
 * chefen bygger listan över tid — utan en ordning står nytillagda moment sist i
 * seq-ordning, vilket ingen kan leta i. localeCompare med 'sv' håller å, ä och ö
 * sist där de hör hemma.
 */
export const iBokstavsordning = (moment: readonly TaskRow[]): readonly TaskRow[] =>
  [...moment].sort((a, b) => a.name.localeCompare(b.name, 'sv'));

/** Samma namn två gånger i listan är ingen faktura kunden begriper. */
export const namnetFinns = (
  moment: readonly TaskRow[],
  namn: string,
  utom: TaskId | null,
): boolean =>
  moment.some(
    (m) => m.id !== utom && m.name.trim().toLowerCase() === namn.trim().toLowerCase(),
  );
